import React from 'react';

/**
 * Card — v2 design-system surface container.
 *
 * Cards on a pure-white page (see Agentlets-Design-Principles.md §4.1)
 * derive their hierarchy from **borders + shadow elevation**, not from a
 * separate surface tone. The default elevation = "sm" + 1px subtle border
 * is the canonical pattern; "md" is reserved for hovered or actively
 * interactive cards; "none" disables elevation for nested cards.
 *
 * Composition:
 *   <Card>
 *     <Card.Header>Title</Card.Header>
 *     <Card.Body>...</Card.Body>
 *     <Card.Footer>...</Card.Footer>
 *   </Card>
 *
 * The compound API is optional — passing children directly into Card
 * works for the common case (just padding + border + shadow).
 *
 * Interactive cards (`interactive` prop or onClick) get hover lift
 * (1px translate + shadow bump) and a focus ring for keyboard users.
 *
 * @param {Object}                       props
 * @param {React.ReactNode}              props.children
 * @param {'none'|'sm'|'md'|'lg'}        [props.elevation='sm']
 * @param {'none'|'sm'|'md'|'lg'}        [props.padding='md']
 * @param {boolean}                      [props.interactive=false]
 *   When true (or when onClick is provided) the card lifts on hover and
 *   becomes keyboard-focusable.
 * @param {(e: React.MouseEvent) => void} [props.onClick]
 * @param {string}                       [props.className]
 * @param {string}                       [props.as='div']
 *   Render element (e.g. "section", "article").
 * @param {string}                       [props.ariaLabel]
 *   Forward additional element props (data-*, role, etc.) via rest.
 */
function CardRoot({
  children,
  elevation = 'sm',
  padding = 'md',
  interactive = false,
  onClick,
  className = '',
  as: As = 'div',
  ariaLabel,
  ...rest
}) {
  const isInteractive = interactive || typeof onClick === 'function';

  const elevationClass = {
    none: '',
    sm:   'shadow-v2-sm',
    md:   'shadow-v2-md',
    lg:   'shadow-v2-lg',
  }[elevation];

  const paddingClass = {
    none: '',
    sm:   'p-3',
    md:   'p-4',
    lg:   'p-5',
  }[padding];

  const interactiveClass = isInteractive
    ? 'v2-focus-ring cursor-pointer transition-[transform,box-shadow,border-color] duration-v2 ease-v2 ' +
      'hover:-translate-y-px hover:shadow-v2-md hover:border-v2-border'
    : '';

  return (
    <As
      onClick={onClick}
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      aria-label={ariaLabel}
      onKeyDown={
        isInteractive && onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick(e);
              }
            }
          : undefined
      }
      className={
        'bg-v2-surface border border-v2-border-subtle rounded-v2-md ' +
        elevationClass + ' ' +
        paddingClass + ' ' +
        interactiveClass + ' ' +
        className
      }
      {...rest}
    >
      {children}
    </As>
  );
}

/**
 * Card.Header — section header inside a Card.
 * Use directly (children freeform) or with `title` + `subtitle` + `actions` shorthand.
 *
 * G301 — `subtitle` added: when title + subtitle are both present the
 * actions row aligns with the title and the subtitle wraps below in
 * muted ink. This matches the Trust Center / Compliance Center tab
 * patterns where every card needs a "what is this" subtitle.
 */
function CardHeader({ children, title, subtitle, actions, className = '', ...rest }) {
  if (title || subtitle || actions) {
    return (
      <div
        className={
          'flex items-start gap-3 pb-3 mb-3 border-b border-v2-border-subtle ' +
          className
        }
        {...rest}
      >
        {(title || subtitle) && (
          <div className="flex-1 min-w-0">
            {title && (
              <h3 className="text-[14px] font-semibold tracking-tight text-v2-ink m-0">
                {title}
              </h3>
            )}
            {subtitle && (
              <p className="text-[12px] text-v2-ink-secondary mt-0.5 m-0">
                {subtitle}
              </p>
            )}
          </div>
        )}
        {actions && <div className="ml-auto flex items-center gap-2 shrink-0">{actions}</div>}
        {children}
      </div>
    );
  }
  return (
    <div
      className={
        'pb-3 mb-3 border-b border-v2-border-subtle ' + className
      }
      {...rest}
    >
      {children}
    </div>
  );
}

function CardBody({ children, className = '', ...rest }) {
  return (
    <div className={className} {...rest}>
      {children}
    </div>
  );
}

function CardFooter({ children, className = '', ...rest }) {
  return (
    <div
      className={
        'flex items-center gap-2 pt-3 mt-3 border-t border-v2-border-subtle ' +
        className
      }
      {...rest}
    >
      {children}
    </div>
  );
}

CardRoot.Header = CardHeader;
CardRoot.Body = CardBody;
CardRoot.Footer = CardFooter;

export const Card = CardRoot;
export default Card;
